import { Controller, type Control, type UseFormSetValue } from 'react-hook-form';
import {
  AppButton,
  AppCheckbox,
  AppForm,
  AppFormLabel,
  AppSelect,
  AppSpin,
  AppTextField,
} from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import { dateValueToIso } from '@/utils/dateConverter';
import type { PatientFormSchemaType } from '@pages/main/patient/types';

interface MedicalAidSectionFormProps {
  control: Control<PatientFormSchemaType>;
  setValue: UseFormSetValue<PatientFormSchemaType>;
  isLoading: boolean;
  isExpired: boolean;
  insurerTypeId: number | null | undefined;
  isNoMedicalAid: boolean;
  isReimbursementMedicalAid: boolean;
  isPartialAidChecked: boolean;
  calcTypeString: string;
  medicalAidInsurerTypeOptions: Array<{ value: number; label: string }>;
  onMedicalAidSearch: () => void;
  onMedicalAidNumberDirectSearch: (number: string) => Promise<void>;
  isMedicalAidDirectSearchLoading?: boolean;
}

export function MedicalAidSectionForm({
  control,
  setValue,
  isLoading,
  isExpired,
  insurerTypeId,
  isNoMedicalAid,
  isReimbursementMedicalAid,
  isPartialAidChecked,
  calcTypeString,
  medicalAidInsurerTypeOptions,
  onMedicalAidSearch,
  onMedicalAidNumberDirectSearch,
  isMedicalAidDirectSearchLoading = false,
}: MedicalAidSectionFormProps) {
  return (
    <AppSpin spinning={isLoading}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-x-6">
          <div className="flex items-start">
            <AppSelect
              value={insurerTypeId}
              onChange={value => {
                const newId = value as number;
                const prevId = insurerTypeId;
                setValue('medical_aid.insurer_type_id', newId);
                // Only clear when the user changes type via this control — not when the modal
                // (or other code) sets insurer_type_id + number together.
                if (prevId !== newId) {
                  setValue('medical_aid.medical_aid_number', '');
                  setValue('medical_aid.medical_aid_patient_number', '');
                  setValue('medical_aid.application_start_date', '');
                  setValue('medical_aid.application_end_date', '');
                  setValue('medical_aid.claim_type_id', null);
                }
              }}
              className="w-full"
              options={medicalAidInsurerTypeOptions}
            />
          </div>

          <div className="grid grid-cols-1 items-start gap-x-4 gap-y-3 sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-x-6">
            <AppFormLabel label="公費負担者番号" />
            <div className="flex flex-wrap items-start gap-2">
              <div className="w-32 shrink-0">
                <Controller
                  name="medical_aid.medical_aid_number"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AppForm.Item
                      className="w-full mb-0"
                      validateStatus={fieldState.invalid ? 'error' : undefined}
                      help={fieldState.error?.message}
                    >
                      <AppTextField
                        {...field}
                        value={field.value ?? ''}
                        className={`w-32 ${isReimbursementMedicalAid ? 'border-pink-500' : ''}`}
                        disabled={isNoMedicalAid}
                        maxLength={255}
                        status={fieldState.invalid ? 'error' : undefined}
                        onBlur={field.onBlur}
                        onChange={e => {
                          field.onChange(e);
                          // User-edited 公費負担番号 invalidates dates loaded from a prior medical aid.
                          setValue('medical_aid.application_start_date', '');
                          setValue('medical_aid.application_end_date', '');
                          setValue('medical_aid.claim_type_id', null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = e.currentTarget.value.trim();
                            if (val) {
                              void onMedicalAidNumberDirectSearch(val);
                            }
                          }
                        }}
                      />
                    </AppForm.Item>
                  )}
                />
              </div>
              <div className="shrink-0">
                <AppButton
                  type="primary"
                  onClick={onMedicalAidSearch}
                  loading={isMedicalAidDirectSearchLoading}
                  data-skip-enter-nav
                >
                  補助入力
                </AppButton>
              </div>
            </div>

            <AppFormLabel label="公費受給者番号" />
            <div className="flex flex-wrap items-start gap-2">
              <div className="w-32 shrink-0">
                <Controller
                  name="medical_aid.medical_aid_patient_number"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AppForm.Item
                      className="w-full mb-0"
                      validateStatus={fieldState.invalid ? 'error' : undefined}
                      help={fieldState.error?.message}
                    >
                      <AppTextField
                        {...field}
                        value={field.value ?? ''}
                        className="w-32"
                        disabled={isNoMedicalAid}
                        maxLength={255}
                        status={fieldState.invalid ? 'error' : undefined}
                      />
                    </AppForm.Item>
                  )}
                />
              </div>
              {isReimbursementMedicalAid && (
                <span className="shrink-0 rounded-sm border border-gray-300 bg-[#eeeeee] px-1 py-0.5 text-xl">
                  償還払い
                </span>
              )}
            </div>
          </div>

          <AppFormLabel label="医療助成負担" />
          <div className="flex flex-wrap items-center gap-2">
            <Controller
              name="medical_aid.insurer_cost_rate"
              control={control}
              render={({ field }) => (
                <AppTextField value={field.value ?? ''} disabled className="w-16 bg-gray-100" />
              )}
            />
            <span className="text-sm">割</span>
            <AppCheckbox checked={isPartialAidChecked} disabled>
              一部助成
            </AppCheckbox>
            {calcTypeString && <span className="text-sm">{calcTypeString}</span>}
            {isExpired && <span className="ml-4 text-xs font-bold text-red-500">有効期限外</span>}
          </div>
        </div>

        {/* HIT-4319: keep each date field in half of the row and wrap errors under its own input. */}
        <div className="grid w-full grid-cols-2 items-start gap-x-6 gap-y-2">
          <div className="flex min-w-0 items-start gap-x-2">
            <AppFormLabel label="適用開始日" />
            <Controller
              name="medical_aid.application_start_date"
              control={control}
              render={({ field, fieldState }) => (
                <AppForm.Item
                  className="mb-0 min-w-0 flex-1"
                  validateStatus={fieldState.invalid ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <RA2NDatePicker
                    value={field.value ?? ''}
                    onChange={val => field.onChange(dateValueToIso(val) ?? '')}
                    className="w-full"
                    placeholder="元号00年00月00日"
                    disabled={isNoMedicalAid}
                    error={fieldState.invalid}
                  />
                </AppForm.Item>
              )}
            />
          </div>

          <div className="flex min-w-0 items-start gap-x-2">
            <AppFormLabel label="適用終了日" />
            <Controller
              name="medical_aid.application_end_date"
              control={control}
              render={({ field, fieldState }) => (
                <AppForm.Item
                  className="mb-0 min-w-0 flex-1"
                  validateStatus={fieldState.invalid ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <RA2NDatePicker
                    value={field.value ?? ''}
                    onChange={val => field.onChange(dateValueToIso(val) ?? '')}
                    className="w-full"
                    placeholder="元号00年00月00日"
                    disabled={isNoMedicalAid}
                    error={fieldState.invalid}
                  />
                </AppForm.Item>
              )}
            />
          </div>

          {isExpired && (
            <div className="w-full mt-2 text-sm font-medium text-red-500">
              有効期限が切れています。
            </div>
          )}
        </div>
      </div>
    </AppSpin>
  );
}
